import { FC, useMemo, useCallback, useState, useEffect } from 'react';
import { useAsync } from 'react-use';
import { BehavioralInsightPresentationMode, EventTypes } from 'modules/domains/segmentation';
import { ensureEnum } from 'utils/tsHelpers';
import { ITERABLE_EVENTS } from 'constants/eventTypes';
import { CustomBehavioralContext } from './CustomBehavioralContext';
import { useBehaviorQueryContext } from './BehaviorQueryContext';
import {
  useAddCustomBehavioralInsight,
  useDeleteCustomBehavioralInsight,
  useCustomBehavioralInsightsQuery,
  useCustomEvents,
} from '../hooks';
import { CustomBehavioralInsight, BehaviorQuery, BehavioralEvent } from '../../domain';
import { getEventInfo } from '../utils';

export const CustomBehavioralContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { customEvents, isLoading } = useCustomEvents();
  const { behavior, refetchBehavior, isBehaviorLoading } = useBehaviorQueryContext();
  const { addCustomBehavioralInsight, isSuccess: customInsightAdded } =
    useAddCustomBehavioralInsight();
  const { deleteCustomBehavioralInsight, isSuccess: customInsightDeleted } =
    useDeleteCustomBehavioralInsight();
  const {
    data: { settings, maxBehavioralInsightSettingsForProject } = {},
    isLoading: isFetchingCustomInsights,
    refetch: refetchCustomInsights,
  } = useCustomBehavioralInsightsQuery();

  /** cached version of custom behavioral insights */
  const [cachedCustomBehavioralInsights, updateCustomBehavioralInsights] = useState<
    CustomBehavioralInsight[]
  >([]);

  /** all iterable events except 'User' and 'CustomEvent' */
  const standardIterableEvents = Object.values(EventTypes).filter(
    (type) => ![EventTypes.User, EventTypes.CustomEvent].includes(type),
  );

  /** all custom events organized by eventName and dataType and sorted alphabetically */
  const allCustomEvents = (customEvents ?? [])
    .map((customEvent) => ({
      event: customEvent,
      dataType: EventTypes.CustomEvent,
    }))
    .sort((a, b) => a.event.localeCompare(b.event));

  /** all standard events organized by name and dataType */
  const allStandardEvents = Object.values(standardIterableEvents).map((event) => ({
    event: ITERABLE_EVENTS[event],
    dataType: ensureEnum(event, EventTypes, EventTypes[event]),
  }));

  /** all current custom behavioral events by name */
  const currentEvents = cachedCustomBehavioralInsights.map(({ event }) => event);

  /** converts "eventName" from type string to a key of BehaviorQuery while also handling undefined */
  const reformattedBehaviorSettings = useMemo(
    () =>
      settings
        ? settings.map(({ eventName, presentationMode, dataType }) => {
            return {
              eventName,
              presentationMode,
              dataType,
            };
          })
        : [],
    [settings, isFetchingCustomInsights],
  );

  /** all events in project excluding current behavioral events */
  const allEventsForProject: BehavioralEvent[] = useMemo(
    () => [...allCustomEvents, ...allStandardEvents],
    [customEvents, isLoading, cachedCustomBehavioralInsights],
  );

  const selectableEventsForProject: BehavioralEvent[] = allEventsForProject.filter(
    ({ event }) => !currentEvents.includes(event),
  );

  useAsync(async () => {
    if (customInsightAdded || customInsightDeleted) {
      await refetchCustomInsights();
      await refetchBehavior();
    }
  }, [customInsightAdded, customInsightDeleted]);

  useEffect(() => {
    if (!isFetchingCustomInsights && !isBehaviorLoading) {
      updateCustomBehavioralInsights(
        reformattedBehaviorSettings
          .map(({ eventName, presentationMode, dataType }) => {
            const event = getEventInfo(dataType, ITERABLE_EVENTS, eventName);
            return {
              event,
              behavior: behavior[event as keyof BehaviorQuery],
              eventName,
              mode: presentationMode,
              dataType,
            };
          })
          .reverse(),
      );
    }
  }, [behavior, settings, isFetchingCustomInsights]);

  const addCustomBehavioralEvent = useCallback(
    (event: string, mode: BehavioralInsightPresentationMode, dataType: EventTypes) => {
      addCustomBehavioralInsight(event, mode, dataType);
    },
    [],
  );

  const deleteCustomBehavioralEvent = useCallback((event: string, dataType: EventTypes) => {
    deleteCustomBehavioralInsight(event, dataType);
    updateCustomBehavioralInsights((previous) =>
      previous.filter(({ eventName }) => eventName !== event),
    );
  }, []);

  const value = {
    addCustomBehavioralEvent,
    deleteCustomBehavioralEvent,
    cachedCustomBehavioralInsights,
    selectableEventsForProject,
    allCustomEvents,
    allStandardEvents,
    maxBehavioralInsightSettingsForProject: maxBehavioralInsightSettingsForProject ?? 0,
  };

  return (
    <CustomBehavioralContext.Provider value={value}>{children}</CustomBehavioralContext.Provider>
  );
};
